import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { OidcProvider } from 'redux-oidc';
import { AnimatePresence } from 'framer-motion';
import { ConfigProvider } from 'antd';
import locale from 'antd/es/locale/pt_BR';

import darkVars from '@/dark.json';
import lightVars from '@/light.json';

import configureStore from './state/store/configureStore';
import userManager from './state/userManager';

import IntlWrapper from './components/_common/IntlWrapper';
import BreadcrumbContextProvider from './contexts/BreadcrumbContextProvider';
import DashboardContexProvider from './contexts/DashboardContexProvider';
import ColorSchemeProvider from './contexts/ColorSchemeContextProvider';
import MainContextProvider from './contexts/MainContextProvider';
import QueryChanelProvider from './contexts/QueryChanelProvider';
import OAuthProvider from './core/libs/OAuth';
import { OAuthCredentials } from './core/libs/OAuth/OAuth.credentials';
import NotificationProvider from './contexts/WebsocketProvider/Notification.provider';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export const store = configureStore();

const Root = ({ children }) => {
  useEffect(() => {
    const currTheme = localStorage.getItem('app-theme');

    const vars = {
      ...(currTheme === 'dark' ? darkVars : lightVars),
      '@white': '#fff',
      '@black': '#000',
    };

    if (!currTheme) localStorage.setItem('app-theme', 'light');

    window.less?.modifyVars(vars).catch((err) => {
      console.error('LESS ERROR AT MOUNT:', err); // eslint-disable-line
    });
  }, []);

  return (
    <ColorSchemeProvider>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <QueryChanelProvider>
            <IntlWrapper>
              <OidcProvider store={store} userManager={userManager}>
                <DashboardContexProvider>
                  <ConfigProvider locale={locale}>
                    <BrowserRouter>
                      <NotificationProvider>
                        <OAuthProvider credentials={OAuthCredentials}>
                          <MainContextProvider>
                            <BreadcrumbContextProvider>
                              <AnimatePresence exitBeforeEnter>
                                {children}
                              </AnimatePresence>
                            </BreadcrumbContextProvider>
                          </MainContextProvider>
                        </OAuthProvider>
                      </NotificationProvider>
                    </BrowserRouter>
                  </ConfigProvider>
                </DashboardContexProvider>
              </OidcProvider>
            </IntlWrapper>
          </QueryChanelProvider>
        </QueryClientProvider>
      </Provider>
    </ColorSchemeProvider>
  );
};

Root.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
    PropTypes.array,
  ]),
};

export default Root;
